import { useAuth } from "@dl/web-core-js/auth";
import { Badge, Button, Input, Switch, toast } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AssemblyVersionClient,
  formatNumber,
  refusalMessage,
  type AssemblyVersionDTO,
  type PackedPieceDTO,
  type ProductStructure,
} from "../../api/manufacturing";

type Draft = Record<number, Record<number, string>>;

const quantityOf = (value: string | undefined) => {
  const number = Number(value ?? "");
  return Number.isFinite(number) && number > 0 ? number : 0;
};

/**
 * Which box each piece of the semi-finished goes in, for one version (design
 * §14): a row per piece, a column per box. Exclusive per unit: a row never
 * places more than the semi-finished has, and what it leaves is shown, with a
 * click to put the rest in a box. A piece the semi-finished does not have
 * (SIGM naming another colourway's) is flagged, to take out.
 */
export function PackingGrid({
  version,
  structure,
  readOnly,
}: {
  version: AssemblyVersionDTO;
  structure: ProductStructure;
  readOnly: boolean;
}) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const queryClient = useQueryClient();
  const client = useMemo(() => new AssemblyVersionClient(http), [http]);
  const key = ["manufacturing", "assembly-version-packing", version.id, "grid"];
  const { data, isLoading } = useQuery({ queryKey: key, queryFn: () => client.packing(version.id) });
  const [draft, setDraft] = useState<Draft>({});
  const [onlyLeft, setOnlyLeft] = useState(false);

  // The draft starts as what is saved, again after every save.
  useEffect(() => {
    if (!data) return;
    const next: Draft = {};
    for (const piece of data.pieces) {
      next[piece.part_id] = Object.fromEntries(Object.entries(piece.boxes).map(([box, quantity]) => [Number(box), String(Number(quantity))]));
    }
    setDraft(next);
  }, [data]);

  const boxes = Array.from({ length: Math.max(1, data?.box_count ?? version.box_count ?? 1) }, (_, index) => index + 1);
  const placedOf = (piece: PackedPieceDTO) => boxes.reduce((sum, box) => sum + quantityOf(draft[piece.part_id]?.[box]), 0);
  const pieces = data?.pieces ?? [];
  const leftOf = (piece: PackedPieceDTO) => Number(piece.made) - placedOf(piece);
  const changed = data != null && pieces.some((piece) =>
    boxes.some((box) => quantityOf(draft[piece.part_id]?.[box]) !== Number(piece.boxes[String(box)] ?? 0)));
  const over = pieces.filter((piece) => Number(piece.made) > 0 && leftOf(piece) < 0);
  const unplaced = pieces.filter((piece) => Number(piece.made) > 0 && leftOf(piece) > 0);
  const foreign = pieces.filter((piece) => Number(piece.made) === 0 && placedOf(piece) > 0);
  const shown = onlyLeft ? pieces.filter((piece) => leftOf(piece) !== 0 || Number(piece.made) === 0) : pieces;

  const set = (part_id: number, box: number, value: string) =>
    setDraft((current) => ({ ...current, [part_id]: { ...(current[part_id] ?? {}), [box]: value } }));
  const putRest = (piece: PackedPieceDTO, box: number) => set(piece.part_id, box, String(quantityOf(draft[piece.part_id]?.[box]) + leftOf(piece)));
  const restAll = (box: number) => {
    for (const piece of pieces) {
      if (Number(piece.made) > 0 && leftOf(piece) > 0) putRest(piece, box);
    }
  };

  const save = useMutation({
    mutationFn: () =>
      client.replacePacking(
        version.id,
        pieces.flatMap((piece) =>
          Number(piece.made) === 0
            ? []
            : boxes.map((box) => ({ part_id: piece.part_id, box_number: box, quantity: quantityOf(draft[piece.part_id]?.[box]) })).filter((row) => row.quantity > 0),
        ),
      ),
    onSuccess: async () => {
      toast.success(t("packing.saved", "Packing list saved"));
      await queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
    },
    onError: (failure) => toast.error(refusalMessage(failure, t("error.generic", "Something went wrong."))),
  });

  if (isLoading || !data) {
    return <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">{t("common.loading", "Loading…")}</p>;
  }
  if (structure.semi_finished_part_id == null) {
    return (
      <p className="px-4 py-3 text-[13px] text-ink-muted lg:px-6">
        {t("packing.no-semi-finished", "The product has no semi-finished: there is nothing to pack. Link one on the Fabrication tab.")}
      </p>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-line px-4 py-2 text-[12px] lg:px-6">
        <span className="text-ink-muted">{t("packing.summary.semi-finished", "Semi-finished")}</span>
        <Link
          to="/manufacturing/parts/$partId"
          params={{ partId: String(structure.semi_finished_part_id) }}
          search={{ tab: "bom" }}
          className="font-medium text-accent underline-offset-2 hover:underline"
        >
          {structure.semi_finished_number}
        </Link>
        <span className="text-ink-muted">{t("packing.summary.pieces", "×1 · {{n}} pieces", { n: pieces.filter((piece) => Number(piece.made) > 0).length })}</span>
        {unplaced.length > 0 && <Badge size="sm" variant="warning">{t("packing.summary.unplaced", "{{n}} in no box", { n: unplaced.length })}</Badge>}
        {over.length > 0 && <Badge size="sm" variant="error">{t("packing.summary.over", "{{n}} packed more than made", { n: over.length })}</Badge>}
        {foreign.length > 0 && <Badge size="sm" variant="error">{t("packing.summary.foreign", "{{n}} not in the semi-finished", { n: foreign.length })}</Badge>}
        {unplaced.length === 0 && over.length === 0 && foreign.length === 0 && (
          <Badge size="sm" variant="success">{t("packing.complete", "Every piece is in a box")}</Badge>
        )}
        <span className="ml-auto flex items-center gap-3">
          <Switch checked={onlyLeft} onChange={setOnlyLeft} label={t("packing.only-left", "Only what is left")} />
          {!readOnly && (
            <>
              <Button size="sm" variant="ghost" disabled={!changed || save.isPending} onClick={() => setDraft(Object.fromEntries(
                pieces.map((piece) => [piece.part_id, Object.fromEntries(Object.entries(piece.boxes).map(([box, quantity]) => [Number(box), String(Number(quantity))]))]),
              ))}>
                {t("packing.undo", "Undo changes")}
              </Button>
              <Button size="sm" variant="primary" disabled={!changed || over.length > 0} loading={save.isPending} onClick={() => save.mutate()}>
                {t("packing.save", "Save")}
              </Button>
            </>
          )}
        </span>
      </div>
      {data.implicit && boxes.length === 1 && (
        <p className="shrink-0 border-b border-line bg-surface-sunk px-4 py-1.5 text-[12px] text-ink-muted lg:px-6">
          {t("packing.one-box.grid", "One box: every piece goes in it, nothing to place. Add a box on the Boxes view to split them.")}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-separate border-spacing-0 text-[13px]">
          <thead className="sticky top-0 z-10 bg-surface-sunk">
            <tr className="text-left text-[11px] uppercase tracking-wide text-ink-muted">
              <th className="border-b border-line px-4 py-1.5 font-medium lg:pl-6">{t("packing.piece", "Piece")}</th>
              <th className="border-b border-line px-2 py-1.5 text-right font-medium">{t("packing.made", "Made")}</th>
              {boxes.map((box) => (
                <th key={box} className="border-b border-line px-2 py-1.5 font-medium">
                  <span className="flex items-center gap-1">
                    {t("bom.section.box", "Box {{n}}", { n: box })}
                    {!readOnly && unplaced.length > 0 && !(data.implicit && boxes.length === 1) && (
                      <Button size="sm" variant="ghost" className="h-5 px-1 text-[11px] normal-case" onClick={() => restAll(box)}>
                        {t("packing.rest-all", "rest here")}
                      </Button>
                    )}
                  </span>
                </th>
              ))}
              <th className="border-b border-line px-2 py-1.5 text-right font-medium">{t("packing.left", "In no box")}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((piece) => {
              const left = leftOf(piece);
              const isForeign = Number(piece.made) === 0;
              return (
                <tr key={piece.part_id} className={isForeign ? "bg-risk/5" : undefined}>
                  <td className="border-b border-line px-4 py-1 lg:pl-6">
                    <Link to="/manufacturing/parts/$partId" params={{ partId: String(piece.part_id) }} className="font-mono text-[12px] text-accent underline-offset-2 hover:underline">
                      {piece.number}
                    </Link>
                    <span className="ml-2 text-ink-muted">{piece.name}</span>
                    {isForeign && (
                      <Badge size="sm" variant="error" className="ml-2">
                        {t("packing.foreign", "Not in the semi-finished")}
                      </Badge>
                    )}
                  </td>
                  <td className="border-b border-line px-2 py-1 text-right tabular-nums">
                    {formatNumber(piece.made)} <span className="text-[11px] text-ink-faint">{piece.unit_code}</span>
                  </td>
                  {boxes.map((box) => (
                    <td key={box} className="border-b border-line px-2 py-1">
                      <span className="flex items-center gap-1">
                        <Input
                          aria-label={t("packing.cell", "{{piece}} in box {{box}}", { piece: piece.number, box })}
                          type="number"
                          min="0"
                          step="any"
                          className="h-7 w-20 py-0"
                          value={draft[piece.part_id]?.[box] ?? ""}
                          disabled={readOnly || isForeign || (data.implicit && boxes.length === 1)}
                          invalid={!isForeign && left < 0 && quantityOf(draft[piece.part_id]?.[box]) > 0}
                          onChange={(event) => set(piece.part_id, box, event.target.value)}
                        />
                        {!readOnly && !isForeign && left > 0 && (
                          <Button size="sm" variant="ghost" className="h-6 px-1 text-[11px]" onClick={() => putRest(piece, box)}>
                            +{formatNumber(left)}
                          </Button>
                        )}
                      </span>
                    </td>
                  ))}
                  <td className="border-b border-line px-2 py-1 text-right tabular-nums">
                    {isForeign ? (
                      !readOnly && (
                        <Button size="sm" variant="ghost" icon={<X className="size-3.5" />} onClick={() => setDraft((current) => ({ ...current, [piece.part_id]: {} }))}>
                          {t("packing.take-out", "Take out")}
                        </Button>
                      )
                    ) : left === 0 ? (
                      <span className="text-ink-faint">0</span>
                    ) : (
                      <span className={left < 0 ? "font-semibold text-risk" : "font-semibold text-warn"}>{formatNumber(left)}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
